import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/client";
import { isMasterAdmin } from "../utils/auth";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";

const EMPTY = {
  counters: {
    valueReclaimed: 0,
    notUtilized: { total: 0, disposalLoss: 0, qualityIssue: 0 },
    utilizationRate: 0,
  },
  perMaterial: [],
  perDonor: [],
};

// compose a cache key that includes the filter params so different filter
// combinations don't collide. keeps each filter's payload cached independently.
const cacheKeyFor = (dateFrom, dateTo, materialsKey, showAll) =>
  `${CACHE_KEYS.RECEIVER_MFCA}:${showAll ? "all" : "mine"}:${dateFrom || ""}:${dateTo || ""}:${materialsKey}`;

export function useReceiverMFCAData({ dateFrom, dateTo, materials, enabled = true } = {}) {
  const { getCachedData, setCachedData, clearCache } = useDataCache();

  // stable signature for array identity — prevents key churn when content unchanged
  const materialsKey = Array.isArray(materials) ? materials.slice().sort().join("|") : "";
  const showAll = isMasterAdmin();
  const cacheKey = cacheKeyFor(dateFrom, dateTo, materialsKey, showAll);

  // refs around cache fns so the effect doesn't churn on context provider re-renders
  const getCacheRef = useRef(getCachedData);
  const setCacheRef = useRef(setCachedData);
  useEffect(() => { getCacheRef.current = getCachedData; }, [getCachedData]);
  useEffect(() => { setCacheRef.current = setCachedData; }, [setCachedData]);

  // lazy initializers pull from cache synchronously on first render so tab-click
  // navigations don't flash a spinner before the effect hydrates
  const [data, setData] = useState(() => {
    if (!enabled) return EMPTY;
    const cached = getCachedData(cacheKey);
    return cached?.data || EMPTY;
  });
  const [loading, setLoading] = useState(() => {
    if (!enabled) return false;
    const cached = getCachedData(cacheKey);
    return !cached;
  });
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(() => {
    if (!enabled) return null;
    const cached = getCachedData(cacheKey);
    return cached?.timestamp || null;
  });

  // bumping refreshKey forces the effect to bypass cache and fetch
  const [refreshKey, setRefreshKey] = useState(0);

  const abortRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    // cache-first: on cache hit AND not a manual refresh, use cached data and
    // skip the fetch entirely. matches useInventorySnapshot / useDonorInventoryData
    // behavior across the rest of the app. fresh data only on filter change
    // (cache key changes → cache miss) or explicit refresh().
    const isManualRefresh = refreshKey > 0;
    if (!isManualRefresh) {
      const cached = getCacheRef.current?.(cacheKey);
      if (cached?.data) {
        setData(cached.data);
        setLastUpdated(cached.timestamp || null);
        setLoading(false);
        return undefined;
      }
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const params = new URLSearchParams();
        if (dateFrom) params.append("date_from", dateFrom);
        if (dateTo) params.append("date_to", dateTo);
        if (materialsKey) params.append("materials", materialsKey.split("|").join(","));
        if (showAll) params.append("show_all", "true");

        const res = await apiFetch(`/receiver/inventory/mfca?${params.toString()}`, {
          method: "GET",
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || "Failed to load MFCA data");
        }
        const body = await res.json();
        const shaped = {
          counters: body.counters || EMPTY.counters,
          perMaterial: body.perMaterial || [],
          perDonor: body.perDonor || [],
        };
        const ts = Date.now();
        setData(shaped);
        setLastUpdated(ts);
        setCacheRef.current?.(cacheKey, shaped, ts);
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("receiver mfca fetch failed:", e);
        setError(e.message || "Unable to load MFCA data");
        setData(EMPTY);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();

    return () => abortRef.current?.abort();
  }, [cacheKey, dateFrom, dateTo, materialsKey, enabled, showAll, refreshKey]);

  // force refresh — used by consumers after writes that invalidate MFCA data
  // (e.g. an acceptance log was saved). clears cache slot for current filters
  // and re-runs the effect with refreshKey bumped so it bypasses the cache check.
  const refresh = useCallback(() => {
    clearCache(cacheKey);
    setRefreshKey((k) => k + 1);
  }, [clearCache, cacheKey]);

  return { data, loading, error, lastUpdated, refresh };
}

export default useReceiverMFCAData;
